import { Select } from "@chakra-ui/react";
import { forwardRef, useMemo } from "react";
import useSWR from "swr";
import { getYearLabel } from "../../../helpers/years/getYearLabel";
import useApiFetcher from "../../../hooks/useApiFetcher";
import generateApiUri from "../../../libraries/utils/generateApiUri";
import { normalizeYearsResponse } from "../../../normalizers/years/normalizeYearsResponse";

const YearSelect = forwardRef(
  /**
   * @typedef {object} Props
   * @property {string | number} [environmentId]
   * @property {boolean} [allowClear]
   *
   * @param {import("@chakra-ui/react").SelectProps & Props} props
   */
  function YearSelect(
    {
      environmentId: _environmentId,
      name,
      value = "",
      allowClear = true,
      ...otherProps
    },
    ref,
  ) {
    const apiFetcher = useApiFetcher();

    const environmentId =
      typeof _environmentId === "number"
        ? String(_environmentId)
        : _environmentId;

    /** @type {import("swr").SWRResponse<import("../../../types/Api/ApiResponse").ApiResponse<import("../../../types/Year").Year[]>>} */
    const { data: _response, isValidating } = useSWR(
      environmentId !== undefined &&
        environmentId !== "" &&
        generateApiUri({
          id: "@years.viewAny",
          query: {
            per_page: 250,
            environment_id: [environmentId],
          },
        }),
      apiFetcher,
    );

    const response = useMemo(() => {
      return _response && normalizeYearsResponse({ response: _response });
    }, [_response]);

    const years = useMemo(() => {
      const years = response?.data ?? [];
      return [...years].sort((a, b) => b.from.localeCompare(a.to));
    }, [response?.data]);

    return (
      <Select
        ref={ref}
        minWidth="250px"
        name={name}
        value={value}
        {...otherProps}
        placeholder={years.length === 0 ? "Aucune" : otherProps.placeholder}
        isDisabled={otherProps.isDisabled || isValidating}>
        {allowClear && years.length > 1 && <option value="">-</option>}

        {years?.map?.((year) => (
          <option value={String(year.id)} key={year.id}>
            {getYearLabel(year)}
          </option>
        ))}
      </Select>
    );
  },
);

export default YearSelect;
